import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ProjectList from './pages/ProjectList';
import ProjectDetail from './pages/ProjectDetail';
import EpisodeDetail from './pages/EpisodeDetail';
import SkillList from './pages/SkillList';
import ScriptSkillList from './pages/ScriptSkillList';
import LanguageSkillList from './pages/LanguageSkillList';
import AdminLanguageList from './pages/AdminLanguageList';
import VoicePresetList from './pages/VoicePresetList';
import Homepage from './pages/Homepage';
import AdminUsers from './pages/AdminUsers';
import AccountGroups from './pages/admin/AccountGroups';
import ProjectMembers from './pages/ProjectMembers';
import ProjectUsage from './pages/ProjectUsage';
import ProjectCredits from './pages/ProjectCredits';
import AnalysisConfig from './pages/AnalysisConfig';
import TemplateList from './pages/TemplateList';
import LLMConfig from './pages/LLMConfig';
import VideoModelConfig from './pages/VideoModelConfig';
import ImageModelConfig from './pages/ImageModelConfig';
import SharePreview from './pages/SharePreview';
import AssetSharePreview from './pages/AssetSharePreview';
import AssetStyleTest from './pages/AssetStyleTest';
import ScriptSummaryPage from './pages/script-summary/ScriptSummaryPage';
import DirectorAnnotationsPage from './pages/director-annotations/DirectorAnnotationsPage';
import UsageSummary from './pages/UsageSummary';
import PlatformReconciliation from './pages/PlatformReconciliation';
import AccountUsage from './pages/AccountUsage';
import MyCredits from './pages/MyCredits';
import Pricing from './pages/Pricing';
import DownloadTool from './pages/DownloadTool';
import DownloadEpisodes from './pages/DownloadEpisodes';
import ServicePricingConfig from './pages/ServicePricingConfig';
import AnnouncementManager from './pages/AnnouncementManager';
import AdminTicketManager from './pages/admin/AdminTicketManager';
import AdminProjectManager from './pages/admin/AdminProjectManager';
import DemoTemplates from './pages/admin/DemoTemplates';
import AdminRechargeRequests from './pages/admin/AdminRechargeRequests';
import ShowcaseManager from './pages/admin/ShowcaseManager';
import ServiceLogs from './pages/ServiceLogs';
import NotificationsPage from './pages/NotificationsPage';
import MyTickets from './pages/MyTickets';
import CreateTicket from './pages/CreateTicket';
import TicketDetail from './pages/TicketDetail';
import AnalyticsDashboard from './pages/admin/AnalyticsDashboard';
import AnalyticsReportPage from './pages/admin/AnalyticsReport';
import TaskQueueDashboard from './pages/admin/TaskQueueDashboard';
import AICallLogs from './pages/admin/AICallLogs';
import TokenMigration from './pages/admin/TokenMigration';
import AssetMerge from './pages/admin/AssetMerge';
import { User, AdminPermission } from './types';
import { hasPermission } from './utils/permissions';
import Forbidden from './pages/Forbidden';
import { ToastProvider } from './components/Toast';
import { NotificationProvider } from './contexts/NotificationContext';
import NotificationBell from './components/NotificationBell';
import { HeaderSlotProvider, HeaderSlotOutlet, HeaderSlotRightOutlet } from './components/HeaderSlotContext';
import { creditsToYuan } from './utils/currency';
import RechargeRequestModal from './components/RechargeRequestModal';

function getCurrentUser(): User | null {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const token = localStorage.getItem('token');
  if (!token) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function PermissionRoute({ group, children }: { group: AdminPermission; children: React.ReactNode }) {
  const user = getCurrentUser();
  if (!hasPermission(user, group)) {
    return <Forbidden />;
  }
  return <>{children}</>;
}

function AppShell({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('common');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [theme, setTheme] = useState<'light' | 'dark'>(() =>
    (localStorage.getItem('theme') as 'light' | 'dark') || 'dark'
  );
  const [user, setUser] = useState<User | null>(getCurrentUser());

  const LANGUAGES = [
    { code: 'zh', name: '中文' },
    { code: 'en', name: 'English' },
  ];

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    document.documentElement.lang = code;
    setShowLanguageMenu(false);
  };

  useEffect(() => {
    import('./api').then(({ getCreditsBalance }) =>
      getCreditsBalance().then(r => setCreditBalance(r.credits)).catch(() => {})
    );
  }, []);

  useEffect(() => {
    import('./api').then(({ getMe }) =>
      getMe().then(me => {
        localStorage.setItem('user', JSON.stringify(me));
        setUser(me);
      }).catch(() => {})
    );
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.lang = i18n.language.startsWith('en') ? 'en' : i18n.language.split('-')[0];
  }, [i18n.language]);

  const toggleTheme = () => {
    setTheme(prev => (prev === 'light' ? 'dark' : 'light'));
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const menuIcon = (d: string) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d={d}/></svg>
  );

  const closeMobileNav = () => setShowMobileNav(false);

  return (
    <HeaderSlotProvider>
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <header className="header" style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <div className="appshell-header-inner">
          <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
            <img src="/VRLOGO.svg" alt="VibeReels" className="appshell-logo" />
            <span style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.02em' }}>
              VibeReels
            </span>
          </Link>

          <div className="appshell-header-slot">
            <HeaderSlotOutlet />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexShrink: 0 }}>
            {/* 页面注入的右侧内容（如本项目消耗药丸），位于用户名左侧 */}
            <HeaderSlotRightOutlet />

            {/* 用户名 + 下拉菜单 */}
            <div className="dropdown">
              <button
                onClick={() => { setShowUserMenu(v => !v); setShowSettingsMenu(false); }}
                className="btn-ghost header-user-btn"
                aria-expanded={showUserMenu}
                aria-haspopup="true"
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.4rem',
                  padding: '0.4rem 0.85rem', borderRadius: '8px',
                  fontSize: '0.9rem', fontWeight: 500, color: 'var(--text-3)',
                  background: 'transparent', border: '1px solid transparent',
                  cursor: 'pointer', transition: 'all var(--transition-fast)',
                }}
              >
                <span className="header-user-name" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                  {user?.username}
                </span>
                {creditBalance !== null && (
                  <span className="header-user-credits" style={{
                    fontSize: '0.8rem',
                    color: creditBalance < 1000 ? 'var(--danger)' : 'var(--success)',
                    background: creditBalance < 1000 ? 'var(--danger-light, rgba(239,68,68,0.1))' : 'var(--success-light, rgba(34,197,94,0.1))',
                    padding: '0.1rem 0.45rem',
                    borderRadius: '10px',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}>
                    {t('balance')} ¥{creditsToYuan(creditBalance)}
                  </span>
                )}
              </button>
              {showUserMenu && (
                <div className="dropdown-menu" role="menu" onMouseLeave={() => { setShowUserMenu(false); setShowLanguageMenu(false); }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--border)' }}>
                    <div>
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-3)' }}>{t('balance')}: </span>
                      <strong style={{ color: creditBalance !== null && creditBalance < 1000 ? 'var(--danger)' : 'var(--text)' }}>{creditBalance !== null ? `¥${creditsToYuan(creditBalance)}` : '-'}</strong>
                    </div>
                    <button className="btn btn-ghost" style={{ fontSize: '0.7rem', padding: '0.1rem 0.4rem', marginLeft: '0.5rem' }} onClick={() => { setShowUserMenu(false); navigate('/my-credits'); }}>
                      {t('details')}
                    </button>
                  </div>
                  <button className="dropdown-item" role="menuitem" onClick={() => { setShowUserMenu(false); setShowRechargeModal(true); }}>
                    {t('rechargeRequest')}
                  </button>
                  <button className="dropdown-item" role="menuitem" onClick={() => { setShowUserMenu(false); navigate('/profile'); }}>{t('profileSettings')}</button>
                  <div className="dropdown-divider" />
                  <button className="dropdown-item" role="menuitem" onClick={handleLogout} style={{ color: 'var(--danger)' }}>{t('logout')}</button>
                </div>
              )}
            </div>

            <RechargeRequestModal open={showRechargeModal} onClose={() => setShowRechargeModal(false)} />

            {/* 语言切换 */}
            <div className="dropdown desktop-only">
              <button
                onClick={() => { setShowLanguageMenu(v => !v); setShowUserMenu(false); setShowSettingsMenu(false); }}
                className="btn-ghost"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: '36px', height: '36px', borderRadius: '8px',
                  background: showLanguageMenu ? 'var(--primary-light)' : 'transparent',
                  border: `1px solid ${showLanguageMenu ? 'var(--primary)' : 'transparent'}`,
                  cursor: 'pointer', transition: 'all var(--transition-fast)',
                  color: showLanguageMenu ? 'var(--primary)' : 'var(--text-3)', padding: 0,
                }}
                title={t('language')}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="2" y1="12" x2="22" y2="12"/>
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                </svg>
              </button>
              {showLanguageMenu && (
                <div className="dropdown-menu" role="menu" onMouseLeave={() => setShowLanguageMenu(false)}>
                  {LANGUAGES.map(lang => (
                    <button
                      key={lang.code}
                      className="dropdown-item"
                      role="menuitem"
                      onClick={() => changeLanguage(lang.code)}
                      style={{
                        fontWeight: i18n.language.startsWith(lang.code) ? 700 : 400,
                        color: i18n.language.startsWith(lang.code) ? 'var(--primary)' : undefined,
                      }}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* 通知铃铛 */}
            <NotificationBell />

            {/* 暗色模式切换 — 移动端隐藏（汉堡菜单已包含主题切换） */}
            <button
              onClick={toggleTheme}
              className="btn-ghost desktop-only"
              style={{
                alignItems: 'center', justifyContent: 'center',
                width: '36px', height: '36px', borderRadius: '8px',
                background: 'transparent', border: '1px solid transparent',
                cursor: 'pointer', transition: 'all var(--transition-fast)',
                color: 'var(--text-3)', padding: 0,
              }}
              title={theme === 'light' ? t('switchToDark') : t('switchToLight')}
            >
              {theme === 'light' ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="5"/>
                  <line x1="12" y1="1" x2="12" y2="3"/>
                  <line x1="12" y1="21" x2="12" y2="23"/>
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                  <line x1="1" y1="12" x2="3" y2="12"/>
                  <line x1="21" y1="12" x2="23" y2="12"/>
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                </svg>
              )}
            </button>

            {/* 设置齿轮图标 + 下拉菜单 — 移动端隐藏（汉堡菜单已包含相同内容） */}
            <div className="dropdown desktop-only">
              <button
                onClick={() => { setShowSettingsMenu(v => !v); setShowUserMenu(false); }}
                aria-expanded={showSettingsMenu}
                aria-haspopup="true"
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: '36px', height: '36px', borderRadius: '8px',
                  background: showSettingsMenu ? 'var(--primary-light)' : 'transparent',
                  border: `1px solid ${showSettingsMenu ? 'var(--primary)' : 'transparent'}`,
                  cursor: 'pointer', transition: 'all var(--transition-fast)',
                  color: showSettingsMenu ? 'var(--primary)' : 'var(--text-3)',
                  padding: 0,
                }}
                title={t('title', { ns: 'settings' })}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"/>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                </svg>
              </button>
              {showSettingsMenu && (
                <div className="dropdown-menu settings-grid-menu" role="menu" onMouseLeave={() => setShowSettingsMenu(false)}>
                  <Link to="/templates" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                    {menuIcon('M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5')}{t('stylePresets', { ns: 'settings' })}
                  </Link>
                  <Link to="/skills" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                    {menuIcon('M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z')}{t('shotSkills', { ns: 'settings' })}
                  </Link>
                  <Link to="/script-skills" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                    {menuIcon('M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8')}{t('scriptSkills', { ns: 'settings' })}
                  </Link>
                  <Link to="/language-skills" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                    {menuIcon('M5 8l6 6 M4 14l6-6 2-3 M2 5h12 M7 2h1 M22 22l-5-10-5 10 M14 18h6')}{t('languageSkills', { ns: 'settings' })}
                  </Link>
                  <Link to="/voice-presets" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                    {menuIcon('M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3zM19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8')}{t('voicePresets', { ns: 'settings' })}
                  </Link>
                  <Link to="/pricing" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                    {menuIcon('M9 5H2v7l6.29 6.29c.94.94 2.48.94 3.42 0l4.58-4.58c.94-.94.94-2.48 0-3.42L9 5zM6 9h.01')}{t('pricing', { ns: 'settings' })}
                  </Link>
                  <Link to="/download-episodes" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                    {menuIcon('M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3')}{t('downloadEpisodes', { ns: 'settings' })}
                  </Link>
                  <Link to="/tickets" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                    {menuIcon('M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z')}{t('myTickets', { ns: 'settings' })}
                  </Link>
                  {hasPermission(user, 'platform_config') && (
                    <>
                      <div className="dropdown-divider" />
                      <Link to="/analysis-config" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                        {menuIcon('M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z')}{t('analysisConfig', { ns: 'settings' })}
                      </Link>
                      <Link to="/llm-config" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                        {menuIcon('M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4')}{t('textModel', { ns: 'settings' })}
                      </Link>
                      <Link to="/video-models" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                        {menuIcon('M15 10l4.553-2.276A1 1 0 0 1 21 8.618v6.764a1 1 0 0 1-1.447.894L15 14M5 18h8a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2z')}{t('videoModel', { ns: 'settings' })}
                      </Link>
                      <Link to="/image-models" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                        {menuIcon('M4 16l4.586-4.586a2 2 0 0 1 2.828 0L16 16m-2-2l1.586-1.586a2 2 0 0 1 2.828 0L20 14m-6-6h.01M6 20h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z')}{t('imageModel', { ns: 'settings' })}
                      </Link>
                    </>
                  )}
                  {hasPermission(user, 'operations') && (
                    <>
                      <div className="dropdown-divider" />
                      <Link to="/admin/announcements" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                        {menuIcon('M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0')}{t('announcements', { ns: 'settings' })}
                      </Link>
                      <Link to="/admin/tickets" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                        {menuIcon('M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z')}{t('ticketManagement', { ns: 'settings' })}
                      </Link>
                      <Link to="/admin/projects" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                        {menuIcon('M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-6l-2-2H5a2 2 0 0 0-2 2z')}{t('projectManagement', { ns: 'settings' })}
                      </Link>
                      <Link to="/admin/demo-templates" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                        {menuIcon('M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-14L4 7m8 4v10M4 7v10l8 4')}{t('demoTemplates', { ns: 'settings' })}
                      </Link>
                      <Link to="/admin/showcase" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                        {menuIcon('M15 10l4.553-2.276A1 1 0 0 1 21 8.618v6.764a1 1 0 0 1-1.447.894L15 14M5 18h8a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2z')}{t('showcaseManagement', { ns: 'settings' })}
                      </Link>
                    </>
                  )}
                  {hasPermission(user, 'billing') && (
                    <>
                      <div className="dropdown-divider" />
                      <Link to="/admin/users" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                        {menuIcon('M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75')}{t('userManagement', { ns: 'settings' })}
                      </Link>
                      <Link to="/admin/recharge-requests" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                        {menuIcon('M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8')}{t('rechargeRequests', { ns: 'settings' })}
                      </Link>
                      <Link to="/service-pricing" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                        {menuIcon('M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z')}{t('servicePricing', { ns: 'settings' })}
                      </Link>
                      <Link to="/admin/analytics" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                        {menuIcon('M9 19v-6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2zm0 0V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v10m-6 0a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2m0 0V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2z')}{t('analytics', { ns: 'settings' })}
                      </Link>
                      <Link to="/usage-summary" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                        {menuIcon('M18 20V10M12 20V4M6 20v-6')}{t('usageSummary', { ns: 'settings' })}
                      </Link>
                      <Link to="/account-usage" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                        {menuIcon('M18 20V10M12 20V4M6 20v-6')}月度对账
                      </Link>
                      <Link to="/admin/account-groups" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                        {menuIcon('M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75')}号组管理
                      </Link>
                      <Link to="/platform-reconciliation" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                        {menuIcon('M9 17V7m0 10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2m0 10a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 7a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2m0 10V7m0 10a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2')}平台对账
                      </Link>
                    </>
                  )}
                  {hasPermission(user, 'monitoring') && (
                    <>
                      <div className="dropdown-divider" />
                      <Link to="/admin/service-logs" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                        {menuIcon('M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M12 18v-6 M8 18v-4 M16 18v-2')}{t('serviceLogs', { ns: 'settings' })}
                      </Link>
                      <Link to="/admin/tasks" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                        {menuIcon('M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2 M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2 M9 5a2 2 0 0 0-2-2h2a2 2 0 0 0 2 2 M9 14l2 2 4-4')}
                        {t('taskQueue', { ns: 'settings', defaultValue: '任务队列' })}
                      </Link>
                      <Link to="/admin/ai-call-logs" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                        {menuIcon('M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M12 18v-6 M8 18v-4 M16 18v-2')}
                        {t('aiCallLogs', { ns: 'settings', defaultValue: '调用审计' })}
                      </Link>
                      <Link to="/admin/token-migration" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                        {menuIcon('M17 1l4 4-4 4 M3 11V9a4 4 0 0 1 4-4h14 M7 23l-4-4 4-4 M21 13v2a4 4 0 0 1-4 4H3')}
                        匹配迁移
                      </Link>
                      <Link to="/admin/asset-merge" onClick={() => setShowSettingsMenu(false)} className="dropdown-item" style={{ textDecoration: 'none' }} target="_blank" rel="noopener noreferrer">
                        {menuIcon('M17 1l4 4-4 4 M3 11V9a4 4 0 0 1 4-4h14 M7 23l-4-4 4-4 M21 13v2a4 4 0 0 1-4 4H3')}
                        合并资产
                      </Link>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* 移动端汉堡菜单按钮 */}
            <button className="mobile-menu-btn" onClick={() => setShowMobileNav(true)}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12"/>
                <line x1="3" y1="6" x2="21" y2="6"/>
                <line x1="3" y1="18" x2="21" y2="18"/>
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* 移动端导航抽屉 */}
      {showMobileNav && (
        <>
          <div className="mobile-nav-overlay" onClick={closeMobileNav} />
          <div className="mobile-nav">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <span style={{ fontWeight: 600, fontSize: '1rem', color: 'var(--text)' }}>{t('menu')}</span>
              <button
                onClick={closeMobileNav}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', padding: '0.25rem' }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <Link to="/" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                {menuIcon('M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z')}{t('home', { ns: 'settings' })}
              </Link>
              <Link to="/templates" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                {menuIcon('M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5')}{t('stylePresets', { ns: 'settings' })}
              </Link>
              <Link to="/skills" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                {menuIcon('M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z')}{t('shotSkills', { ns: 'settings' })}
              </Link>
              <Link to="/script-skills" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                {menuIcon('M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8')}{t('scriptSkills', { ns: 'settings' })}
              </Link>
              <Link to="/language-skills" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                {menuIcon('M5 8l6 6 M4 14l6-6 2-3 M2 5h12 M7 2h1 M22 22l-5-10-5 10 M14 18h6')}{t('languageSkills', { ns: 'settings' })}
              </Link>
              <Link to="/voice-presets" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                {menuIcon('M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3zM19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8')}{t('voicePresets', { ns: 'settings' })}
              </Link>
              <Link to="/pricing" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                {menuIcon('M9 5H2v7l6.29 6.29c.94.94 2.48.94 3.42 0l4.58-4.58c.94-.94.94-2.48 0-3.42L9 5zM6 9h.01')}{t('pricing', { ns: 'settings' })}
              </Link>
              <Link to="/download-episodes" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                {menuIcon('M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3')}{t('downloadEpisodes', { ns: 'settings' })}
              </Link>
              <Link to="/tickets" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                {menuIcon('M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z')}{t('myTickets', { ns: 'settings' })}
              </Link>
              {hasPermission(user, 'platform_config') && (
                <>
                  <div className="dropdown-divider" />
                  <Link to="/analysis-config" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                    {menuIcon('M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z')}{t('analysisConfig', { ns: 'settings' })}
                  </Link>
                  <Link to="/llm-config" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                    {menuIcon('M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4')}{t('llmConfig', { ns: 'settings' })}
                  </Link>
                  <Link to="/video-models" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                    {menuIcon('M15 10l4.553-2.276A1 1 0 0 1 21 8.618v6.764a1 1 0 0 1-1.447.894L15 14M5 18h8a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2z')}{t('videoModel', { ns: 'settings' })}
                  </Link>
                  <Link to="/image-models" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                    {menuIcon('M4 16l4.586-4.586a2 2 0 0 1 2.828 0L16 16m-2-2l1.586-1.586a2 2 0 0 1 2.828 0L20 14m-6-6h.01M6 20h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2z')}{t('imageModel', { ns: 'settings' })}
                  </Link>
                </>
              )}
              {hasPermission(user, 'operations') && (
                <>
                  <div className="dropdown-divider" />
                  <Link to="/admin/announcements" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                    {menuIcon('M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0')}{t('announcements', { ns: 'settings' })}
                  </Link>
                  <Link to="/admin/tickets" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                    {menuIcon('M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z')}{t('ticketManagement', { ns: 'settings' })}
                  </Link>
                  <Link to="/admin/projects" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                    {menuIcon('M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-6l-2-2H5a2 2 0 0 0-2 2z')}{t('projectManagement', { ns: 'settings' })}
                  </Link>
                  <Link to="/admin/demo-templates" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                    {t('demoTemplates', { ns: 'settings' })}
                  </Link>
                  <Link to="/admin/showcase" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                    {menuIcon('M15 10l4.553-2.276A1 1 0 0 1 21 8.618v6.764a1 1 0 0 1-1.447.894L15 14M5 18h8a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2z')}{t('showcaseManagement', { ns: 'settings' })}
                  </Link>
                </>
              )}
              {hasPermission(user, 'billing') && (
                <>
                  <div className="dropdown-divider" />
                  <Link to="/admin/users" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                    {menuIcon('M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75')}{t('userManagement', { ns: 'settings' })}
                  </Link>
                  <Link to="/admin/recharge-requests" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                    {menuIcon('M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8')}{t('rechargeRequests', { ns: 'settings' })}
                  </Link>
                  <Link to="/service-pricing" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                    {menuIcon('M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z')}{t('servicePricing', { ns: 'settings' })}
                  </Link>
                  <Link to="/admin/analytics" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                    {menuIcon('M9 19v-6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2zm0 0V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v10m-6 0a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2m0 0V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2z')}{t('analytics', { ns: 'settings' })}
                  </Link>
                  <Link to="/usage-summary" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                    {menuIcon('M18 20V10M12 20V4M6 20v-6')}{t('usageSummary', { ns: 'settings' })}
                  </Link>
                  <Link to="/account-usage" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                    {menuIcon('M18 20V10M12 20V4M6 20v-6')}月度对账
                  </Link>
                  <Link to="/admin/account-groups" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                    {menuIcon('M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75')}号组管理
                  </Link>
                  <Link to="/platform-reconciliation" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                    {menuIcon('M9 17V7m0 10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2m0 10a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 7a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2m0 10V7m0 10a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2')}平台对账
                  </Link>
                </>
              )}
              {hasPermission(user, 'monitoring') && (
                <>
                  <div className="dropdown-divider" />
                  <Link to="/admin/service-logs" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                    {menuIcon('M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M12 18v-6 M8 18v-4 M16 18v-2')}{t('serviceLogs', { ns: 'settings' })}
                  </Link>
                  <Link to="/admin/tasks" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                    {menuIcon('M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2 M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2 M9 5a2 2 0 0 0-2-2h2a2 2 0 0 0 2 2 M9 14l2 2 4-4')}
                    {t('taskQueue', { ns: 'settings', defaultValue: '任务队列' })}
                  </Link>
                  <Link to="/admin/ai-call-logs" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                    {menuIcon('M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6 M12 18v-6 M8 18v-4 M16 18v-2')}
                    {t('aiCallLogs', { ns: 'settings', defaultValue: '调用审计' })}
                  </Link>
                  <Link to="/admin/token-migration" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                    {menuIcon('M17 1l4 4-4 4 M3 11V9a4 4 0 0 1 4-4h14 M7 23l-4-4 4-4 M21 13v2a4 4 0 0 1-4 4H3')}
                    匹配迁移
                  </Link>
                  <Link to="/admin/asset-merge" onClick={closeMobileNav} className="dropdown-item" style={{ textDecoration: 'none' }}>
                    {menuIcon('M17 1l4 4-4 4 M3 11V9a4 4 0 0 1 4-4h14 M7 23l-4-4 4-4 M21 13v2a4 4 0 0 1-4 4H3')}
                    合并资产
                  </Link>
                </>
              )}
              <div className="dropdown-divider" />
              <button className="dropdown-item" onClick={() => { closeMobileNav(); navigate('/my-credits'); }}>
                {menuIcon('M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6')}{t('myBalance', { ns: 'settings' })}
              </button>
              <button className="dropdown-item" onClick={() => { closeMobileNav(); setShowRechargeModal(true); }}>
                {t('rechargeRequest')}
              </button>
              <button className="dropdown-item" onClick={() => { closeMobileNav(); navigate('/profile'); }}>
                {menuIcon('M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2')}{t('profileSettings')}
              </button>
              <button className="dropdown-item" onClick={toggleTheme}>
                {theme === 'light'
                  ? menuIcon('M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z')
                  : menuIcon('M12 12m-5 0a5 5 0 1 0 10 0 5 5 0 1 0-10 0')
                }
                {theme === 'light' ? t('darkMode', { ns: 'settings' }) : t('lightMode', { ns: 'settings' })}
              </button>
              <button className="dropdown-item" onClick={() => { closeMobileNav(); handleLogout(); }} style={{ color: 'var(--danger)' }}>
                {menuIcon('M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9')}{t('logout')}
              </button>
            </div>
          </div>
        </>
      )}

      <main style={{ flex: 1 }}>
        {children}
      </main>
    </div>
    </HeaderSlotProvider>
  );
}

function ProfilePage() {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const [currentUser, setCurrentUser] = useState<User | null>(() => getCurrentUser());
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [pwdError, setPwdError] = useState('');
  const [pwdSuccess, setPwdSuccess] = useState('');
  const [pwdSaving, setPwdSaving] = useState(false);

  const [phone, setPhone] = useState(currentUser?.phone || '');
  const [phoneError, setPhoneError] = useState('');
  const [phoneSuccess, setPhoneSuccess] = useState('');
  const [phoneSaving, setPhoneSaving] = useState(false);

  const handlePwdSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!oldPwd || !newPwd) { setPwdError(t('fillAllFields')); return; }
    setPwdSaving(true);
    setPwdError(''); setPwdSuccess('');
    try {
      const { changePassword } = await import('./api');
      await changePassword(oldPwd, newPwd);
      setPwdSuccess(t('profile.passwordChanged'));
      setOldPwd(''); setNewPwd('');
    } catch (err: any) {
      setPwdError(err.message || t('profile.changeFailed'));
    } finally {
      setPwdSaving(false);
    }
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = phone.trim();
    if (!trimmed) { setPhoneError(t('profile.phoneEmpty')); return; }
    if (!/^1[3-9]\d{9}$/.test(trimmed)) { setPhoneError(t('profile.phoneInvalid')); return; }
    setPhoneSaving(true);
    setPhoneError(''); setPhoneSuccess('');
    try {
      const { changeMyPhone } = await import('./api');
      const updated = await changeMyPhone(trimmed);
      if (updated && updated.id) {
        localStorage.setItem('user', JSON.stringify(updated));
        setCurrentUser(updated);
      }
      setPhoneSuccess(t('profile.phoneChanged'));
    } catch (err: any) {
      setPhoneError(err.message || t('profile.changeFailed'));
    } finally {
      setPhoneSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: '480px', margin: '3rem auto', padding: '0 1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
        <button className="btn btn-secondary" style={{ fontSize: '0.85rem' }} onClick={() => navigate(-1)}>{t('profile.goBack')}</button>
        <h1 style={{ margin: 0, fontSize: '1.5rem' }}>{t('profile.title')}</h1>
      </div>

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ margin: '0 0 1rem', fontSize: '1.1rem' }}>{t('profile.changePassword')}</h3>
        <form onSubmit={handlePwdSubmit}>
          <div className="form-group">
            <label className="form-label">{t('profile.oldPassword')}</label>
            <input className="input" type="password" value={oldPwd} onChange={e => setOldPwd(e.target.value)} placeholder={t('profile.enterCurrentPassword')} />
          </div>
          <div className="form-group">
            <label className="form-label">{t('profile.newPassword')}</label>
            <input className="input" type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} placeholder={t('profile.enterNewPassword')} />
          </div>
          {pwdError && <p className="error-text">{pwdError}</p>}
          {pwdSuccess && <p style={{ color: 'var(--success)', fontSize: '0.875rem' }}>{pwdSuccess}</p>}
          <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: '0.5rem' }} disabled={pwdSaving}>
            {pwdSaving ? t('profile.saving') : t('profile.savePassword')}
          </button>
        </form>
      </div>

      <div className="card">
        <h3 style={{ margin: '0 0 1rem', fontSize: '1.1rem' }}>{t('profile.changePhone')}</h3>
        <form onSubmit={handlePhoneSubmit}>
          <div className="form-group">
            <label className="form-label">{t('profile.phone')} {!currentUser?.phone && <span style={{ color: 'var(--danger)', fontSize: '0.8rem' }}>{t('profile.phoneNotSet')}</span>}</label>
            <input className="input" type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder={t('profile.enterPhone')} />
          </div>
          {phoneError && <p className="error-text">{phoneError}</p>}
          {phoneSuccess && <p style={{ color: 'var(--success)', fontSize: '0.875rem' }}>{phoneSuccess}</p>}
          <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: '0.5rem' }} disabled={phoneSaving}>
            {phoneSaving ? t('profile.saving') : t('profile.savePhone')}
          </button>
        </form>
      </div>
    </div>
  );
}

function App() {
  return (
    <ToastProvider>
      <Routes>
        <Route path="/" element={<Homepage />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/share/:code" element={<SharePreview />} />
        <Route path="/asset-share/:code" element={<AssetSharePreview />} />
        <Route path="/*" element={
          <ProtectedRoute>
            <NotificationProvider>
            <AppShell>
              <Routes>
                <Route path="/projects" element={<ProjectList />} />
                <Route path="/projects/:id" element={<ProjectDetail />} />
                <Route path="/projects/:id/members" element={<ProjectMembers />} />
              <Route path="/projects/:id/usage" element={<PermissionRoute group="billing"><ProjectUsage /></PermissionRoute>} />
              <Route path="/projects/:id/credits" element={<ProjectCredits />} />
              <Route path="/projects/:id/style-test" element={<AssetStyleTest />} />
              <Route path="/projects/:id/script-summary" element={<ScriptSummaryPage />} />
              <Route path="/projects/:projectId/episodes/:id/director-annotations" element={<DirectorAnnotationsPage />} />
              <Route path="/projects/:projectId/episodes/:id" element={<EpisodeDetail />} />
              <Route path="/episodes/:id" element={<EpisodeDetail />} />
                <Route path="/templates" element={<TemplateList />} />
                <Route path="/skills" element={<SkillList />} />
                <Route path="/script-skills" element={<ScriptSkillList />} />
                <Route path="/language-skills" element={<LanguageSkillList />} />
                <Route path="/voice-presets" element={<VoicePresetList />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/my-credits" element={<MyCredits />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/download-tool" element={<DownloadTool />} />
                <Route path="/download-episodes" element={<DownloadEpisodes />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/tickets" element={<MyTickets />} />
                <Route path="/tickets/new" element={<CreateTicket />} />
                <Route path="/tickets/:id" element={<TicketDetail />} />
                <Route path="/analysis-config" element={
                  <PermissionRoute group="platform_config">
                    <AnalysisConfig />
                  </PermissionRoute>
                } />
                <Route path="/llm-config" element={
                  <PermissionRoute group="platform_config">
                    <LLMConfig />
                  </PermissionRoute>
                } />
                <Route path="/video-models" element={
                  <PermissionRoute group="platform_config">
                    <VideoModelConfig />
                  </PermissionRoute>
                } />
                <Route path="/image-models" element={
                  <PermissionRoute group="platform_config">
                    <ImageModelConfig />
                  </PermissionRoute>
                } />
                <Route path="/service-pricing" element={
                  <PermissionRoute group="billing">
                    <ServicePricingConfig />
                  </PermissionRoute>
                } />
                <Route path="/usage-summary" element={
                  <PermissionRoute group="billing">
                    <UsageSummary />
                  </PermissionRoute>
                } />
                <Route path="/platform-reconciliation" element={
                  <PermissionRoute group="billing">
                    <PlatformReconciliation />
                  </PermissionRoute>
                } />
                <Route path="/account-usage" element={
                  <PermissionRoute group="billing">
                    <AccountUsage />
                  </PermissionRoute>
                } />
                <Route path="/admin/users" element={
                  <PermissionRoute group="billing">
                    <AdminUsers />
                  </PermissionRoute>
                } />
                <Route path="/admin/account-groups" element={
                  <PermissionRoute group="billing">
                    <AccountGroups />
                  </PermissionRoute>
                } />
                <Route path="/admin/recharge-requests" element={
                  <PermissionRoute group="billing">
                    <AdminRechargeRequests />
                  </PermissionRoute>
                } />
                <Route path="/admin/languages" element={
                  <PermissionRoute group="platform_config">
                    <AdminLanguageList />
                  </PermissionRoute>
                } />
                <Route path="/admin/announcements" element={
                  <PermissionRoute group="operations">
                    <AnnouncementManager />
                  </PermissionRoute>
                } />
                <Route path="/admin/tickets" element={
                  <PermissionRoute group="operations">
                    <AdminTicketManager />
                  </PermissionRoute>
                } />
                <Route path="/admin/projects" element={
                  <PermissionRoute group="operations">
                    <AdminProjectManager />
                  </PermissionRoute>
                } />
                <Route path="/admin/demo-templates" element={
                  <PermissionRoute group="operations">
                    <DemoTemplates />
                  </PermissionRoute>
                } />
                <Route path="/admin/token-migration" element={
                  <PermissionRoute group="monitoring">
                    <TokenMigration />
                  </PermissionRoute>
                } />
                <Route path="/admin/asset-merge" element={
                  <PermissionRoute group="monitoring">
                    <AssetMerge />
                  </PermissionRoute>
                } />
                <Route path="/admin/showcase" element={
                  <PermissionRoute group="operations">
                    <ShowcaseManager />
                  </PermissionRoute>
                } />
                <Route path="/admin/analytics" element={
                  <PermissionRoute group="billing">
                    <AnalyticsDashboard />
                  </PermissionRoute>
                } />
                <Route path="/admin/analytics/report/:id" element={
                  <PermissionRoute group="billing">
                    <AnalyticsReportPage />
                  </PermissionRoute>
                } />
                <Route path="/admin/service-logs" element={
                  <PermissionRoute group="monitoring">
                    <ServiceLogs />
                  </PermissionRoute>
                } />
                <Route path="/admin/tasks" element={
                  <PermissionRoute group="monitoring">
                    <TaskQueueDashboard />
                  </PermissionRoute>
                } />
                <Route path="/admin/ai-call-logs" element={
                  <PermissionRoute group="monitoring">
                    <AICallLogs />
                  </PermissionRoute>
                } />
              </Routes>
            </AppShell>
            </NotificationProvider>
          </ProtectedRoute>
        } />
      </Routes>
    </ToastProvider>
  );
}

export default App;
